import { PLATFORM } from 'aurelia-pal';
import { olapicSSOClient } from 'olapic-sso-client';
/**
 * @typedef {Object} AppSessionSubscription
 * @property {Function} dispose The function to unsubscribe from the event.
 * @access protected
 */

/**
 * A helper class to interact with the Olapic SSO service.
 */
class AppSession {
  /**
   * @param {EventAggregator}  eventAggregator  To subscribe to custom user events the SSO
   *                                            doesn't track.
   * @param {AppConfiguration} appConfiguration To read the SSO settings.
   * @param {SessionEvents}    SESSION_EVENTS   To get the names of custom events the SSO doesn't
   *                                            track.
   */
  constructor(eventAggregator, appConfiguration, SESSION_EVENTS) {
    'inject';

    /**
     * A local reference for the `eventAggregator` service.
     * @type {EventAggregator}
     * @access protected
     */
    this._eventAggregator = eventAggregator;
    /**
     * A local reference for the `appConfiguration` service.
     * @type {AppConfiguration}
     * @access protected
     */
    this._appConfiguration = appConfiguration;
    /**
     * A local reference for the `SESSION_EVENTS` constant.
     * @type {SessionEvents}
     * @access protected
     */
    this._SESSION_EVENTS = SESSION_EVENTS;

    this._configureSSOClient();
  }
  /**
   * Starts the authentication process.
   * @return {Promise<Object,Error>}
   */
  authenticate() {
    olapicSSOClient.setConfig({
      callback: PLATFORM.location.href,
    });

    return olapicSSOClient.authenticate();
  }
  /**
   * Gets the user session information.
   * @return {Object}
   */
  getSession() {
    return olapicSSOClient.getSession();
  }
  /**
   * Checks whether or not the user is authenticated.
   * @return {Boolean}
   */
  isAuthenticated() {
    return olapicSSOClient.isAuthenticated();
  }
  /**
   * Adds a listener function for when the user session information changes.
   * @param {Function} fn The function to call when the event is triggered.
   * @return {AppSessionSubscription}
   */
  onSessionChange(fn) {
    return this._subscribe(olapicSSOClient.EVENTS.session.change, fn);
  }
  /**
   * Adds a listener function for when the user session is created.
   * @param {Function} fn The function to call when the event is triggered.
   * @return {AppSessionSubscription}
   */
  onSessionCreated(fn) {
    return this._subscribe(olapicSSOClient.EVENTS.session.creationSuccess, fn);
  }
  /**
   * Adds a listener function for when the user session is successfully created.
   * @param {Function} fn The function to call when the event is triggered.
   * @return {AppSessionSubscription}
   */
  onSessionCreationFinishes(fn) {
    return this._subscribe(olapicSSOClient.EVENTS.session.creationFinish, fn);
  }
  /**
   * Adds a listener function for when the user session is authenticated and the session is about
   * to be created.
   * @param {Function} fn The function to call when the event is triggered.
   * @return {AppSessionSubscription}
   */
  onSessionCreationStarts(fn) {
    return this._subscribe(olapicSSOClient.EVENTS.session.creationStart, fn);
  }
  /**
   * Adds a listener function for when the user session is destroyed.
   * @param {Function} fn The function to call when the event is triggered.
   * @return {AppSessionSubscription}
   */
  onSessionEnd(fn) {
    return this._subscribeOnce(olapicSSOClient.EVENTS.session.end, fn);
  }
  /**
   * Adds a listener function for when the user signs out from another app.
   * @param {Function} fn The function to call when the event is triggered.
   * @return {AppSessionSubscription}
   */
  onSessionInterrupted(fn) {
    return this._subscribeOnce(olapicSSOClient.EVENTS.session.interrupted, fn);
  }
  /**
   * Adds a listener function for when the user session is loaded from the storage.
   * @param {Function} fn The function to call when the event is triggered.
   * @return {AppSessionSubscription}
   */
  onSessionLoaded(fn) {
    return this._subscribe(olapicSSOClient.EVENTS.session.load, fn);
  }
  /**
   * Adds a listener function for when the user token is invalid.
   * @param {Function} fn The function to call when the event is triggered.
   * @return {Subscription}
   */
  onSessionUnauthorizedToken(fn) {
    return this._eventAggregator.subscribeOnce(this._SESSION_EVENTS.unauthorized, fn);
  }
  /**
   * Triggers the sign out process.
   * @return {Promise<undefined,Error>}
   */
  signout() {
    return olapicSSOClient.signout();
  }
  /**
   * Setups the SSO configuration.
   * @access protected
   */
  _configureSSOClient() {
    const sso = this._appConfiguration.get('sso');
    olapicSSOClient.setClientConfig({
      api: {
        url: sso.url,
      },
    });
    olapicSSOClient.setConfig(sso.settings);
  }
  /**
   * A helper method to create a subscription to an SSO event and wrap the returned object on
   * an Aurelia's Subscription-like object.
   * @param {string}   eventName The name of the event.
   * @param {Function} fn        The function to call when the event is triggered.
   * @return {AppSessionSubscription}
   * @access protected
   */
  _subscribe(eventName, fn) {
    return this._wrap(olapicSSOClient.on(eventName, fn));
  }
  /**
   * A helper method to create a one time subscription to an SSO event and wrap the returned
   * object on an Aurelia's Subscription-like object.
   * @param {string}   eventName The name of the event.
   * @param {Function} fn        The function to call when the event is triggered.
   * @return {AppSessionSubscription}
   * @access protected
   */
  _subscribeOnce(eventName, fn) {
    return this._wrap(olapicSSOClient.once(eventName, fn));
  }
  /**
   * Wraps an SSO event subscription, which is a function to unsubscribe, into an Aurelia's
   * Subscription-like object.
   * @param {Function} subscription The SSO function to unsubscribe from an event.
   * @return {AppSessionSubscription}
   * @access protected
   */
  _wrap(subscription) {
    return {
      dispose: subscription,
    };
  }
}

export { AppSession };
